import { useMemo, useRef, useState } from 'react'

import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'

import ReplayTaskDescription from './components/ReplayTaskDescription'
import ReplayTestCases from './components/ReplayTestCases'
import ReplayCodeView from './components/ReplayCodeView'
import ReplayTerminal from './components/ReplayTerminal'
import ReplayChatWindow from './components/ReplayChatWindow'
import ReplayLivePreview from './components/ReplayLivePreview'
import ReplayMouseLayer from './components/ReplayMouseLayer'
import ReplayControls from './components/ReplayControls'
import { ReplayNotice, ReplayExplanation, ReplayAwayOverlay } from './components/ReplayAnnotations'

import { usePanelResize } from '../workspace/hooks/usePanelResize'
import { useReplayState } from './hooks/useReplayState'
import { changedLineNumbers } from './utils/textDiff'

import { HIDE_REPLAY_CONTROLS } from './ReplayWorkspacePage'

import '../workspace/css/workspace.scss'

const formatClock = (time) => new Date(time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })

// The optional props below are for pages replayed through an adapter (e.g. Reasoning Gap,
// see reasoningGap/reasoningGapReplay.js); their defaults keep the workspace replay as is.
// explanationPrompt: non-null shows an "Explain your thinking" panel under the code.
// chatWelcome: null for a chat that had no welcome message. showMouse: false when the page
// logged no mouse movement, so a cursor parked mid-screen isn't mistaken for the student's.
// initialSizes: starting panel sizes, see usePanelResize. showNavbar: false drops the
// decorative app bar. showChanges: highlight what the latest change added: changed code
// lines and explanation words (non-GUI layout only), and new chat messages.
// formatText: how the student's words are shown - the code (non-GUI layout), the terminal's
// lines, the chat's messages and its input box - as written unless given one (the experiment
// Overview passes its word filter). Each text goes through it as it comes on screen.
export default function ReplayWorkspace({ playback, events = [], startTime = 0, task = '', testCases = [], starterCode = '', studentName = 'Student', isGuiSession = false, guiConfig = null, explanationPrompt = null, testCasesTitle, runLabel, chatWelcome, showMouse = true, initialSizes, showNavbar = true, showChanges = false, formatText = null }) {
  const containerRef = useRef(null)
  const layout = usePanelResize(containerRef, initialSizes)
  // Only meaningful for GUI sessions (see the tab bar below) - Live Preview first, matching
  // what an instructor most often wants to check when reviewing a GUI submission.
  const [rightPanelTab, setRightPanelTab] = useState('preview')

  const {
    mousePos,
    isClicking,
    code,
    activeFile,
    ideSplitPercent,
    jsViewSplitPercent,
    terminalOutput,
    isRunning,
    testResults,
    messages,
    currentInput,
    isAiProcessing,
    explanation,
    lastChange,
    notice,
    away,
  } = useReplayState(events, playback.currentTime, startTime, starterCode)

  // What is on screen, through formatText when given (it keeps a text's lines, so changedLines
  // still match the code shown).
  const shownCode = useMemo(() => (formatText && !isGuiSession ? formatText(code) : code), [formatText, isGuiSession, code])
  const shownTerminal = useMemo(
    () => (formatText ? terminalOutput.map((line) => (typeof line.content === 'string' ? { ...line, content: formatText(line.content) } : line)) : terminalOutput),
    [formatText, terminalOutput]
  )
  const shownMessages = useMemo(
    () => (formatText ? messages.map((message) => (typeof message.content === 'string' ? { ...message, content: formatText(message.content) } : message)) : messages),
    [formatText, messages]
  )
  const shownInput = useMemo(() => (formatText && currentInput ? formatText(currentInput) : currentInput), [formatText, currentInput])

  const change = showChanges ? lastChange : null
  const codeBefore = change?.codeBefore ?? null
  const changedLines = useMemo(
    () => (codeBefore == null ? null : changedLineNumbers(codeBefore, code)),
    [codeBefore, code]
  )
  const editCaption = change ? `Green: changed at ${formatClock(change.time)}` : null
  const newMessageIds = change?.newMessages ? { from: change.messagesBefore + 1, to: change.messagesBefore + change.newMessages } : null

  // InitialFinal/InitialFinalCSS both hide the grading-focused left panel and terminal live
  // (nothing is graded in either) and use a completely different middle/right arrangement -
  // this used to always fall through to the generic three-tab template below regardless of
  // guiConfig, which is why replay showed Test Cases and an HTML/CSS/JS tab bar for sessions
  // that never had either on screen, and no Target pane at all.
  const isCompareLayout = guiConfig?.layout === 'compare'
  const isCompareImageLayout = guiConfig?.layout === 'compare-image'
  const isCompareJsLayout = guiConfig?.layout === 'compare-js'
  const hidesGradingPanels = isCompareLayout || isCompareImageLayout || isCompareJsLayout

  let parsedCode = { html: '', css: '', js: '' }
  try {
    const parsed = JSON.parse(code)
    if (parsed && typeof parsed === 'object') parsedCode = parsed
  } catch (e) {
    // code isn't valid JSON yet (no CODE_* event logged) - render blank panes rather than throw.
  }

  // InitialFinal's Target is live-rendered HTML sharing the starter's CSS (see
  // guiSessionConfig.js's INITIAL_FINAL_SHARED_CSS comment) - reuses ReplayLivePreview by
  // constructing the same {html,css,js} shape it already expects, rather than a second
  // rendering component.
  const targetCodeForCompareLayout = isCompareLayout
    ? JSON.stringify({ html: guiConfig?.finalHtml || '', css: parsedCode.css || '', js: '' })
    : null

  // InitialFinalJS's Target pane uses the STARTER html/css (not parsedCode.css/the
  // student's live-edited CSS, unlike targetCodeForCompareLayout above) - CSS is
  // student-editable for this type, so reusing the student's current CSS here would make
  // the Target reference drift as they type, unlike InitialFinal (HTML-only editable)
  // where the CSS pane is always empty anyway. Only the JS differs from the starter.
  let starterParsedForCompareJs = { html: '', css: '', js: '' }
  if (isCompareJsLayout) {
    try {
      const parsed = JSON.parse(starterCode)
      if (parsed && typeof parsed === 'object') starterParsedForCompareJs = parsed
    } catch (e) {
      // Malformed/missing starter code - the Target pane just renders blank.
    }
  }
  const targetCodeForCompareJsLayout = isCompareJsLayout
    ? JSON.stringify({ html: starterParsedForCompareJs.html || '', css: starterParsedForCompareJs.css || '', js: guiConfig?.finalJs || '' })
    : null

  return (
    <div className="ws-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      
      {/* Navbar */}
      {showNavbar && <AppBar position="static" color="primary" elevation={0}>
        <Toolbar 
          disableGutters 
          sx={{ 
            minHeight: '64px',
            paddingLeft: '24px', 
          }}
        >
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.1rem',
              color: 'inherit',
              textDecoration: 'none',
              userSelect: 'none'
            }}
          >
            VizPI
          </Typography>
        </Toolbar>
      </AppBar>}

      {/* Main Content */}
      <div 
        className="ws-content" 
        ref={containerRef}
        style={{ flex: 1, position: 'relative', overflow: 'hidden' }}
      >
        {showMouse && <ReplayMouseLayer mousePos={mousePos} isClicking={isClicking} />}
        <ReplayAwayOverlay away={away} />

        {/* Left Panel - hidden for InitialFinal/InitialFinalCSS, same as the live workspace's
            hidesGradingPanels (nothing is graded, and Task Description moves into the middle
            panel instead for these two layouts - see below). */}
        {!hidesGradingPanels && (
          <>
            <div className="ws-panel-left" style={{ width: `${layout.leftWidth}px` }}>
              <div className="ws-panel-content">
                <ReplayTaskDescription task={task} />
                <div className="ws-section-divider" />
                <ReplayTestCases testResults={testResults} testCases={testCases} title={testCasesTitle} />
              </div>
            </div>
            <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingLeft} />
          </>
        )}

        {/* Left Panel - InitialFinalJS only: mirrors the live Workspace.jsx's left-panel
            "Your Code" editor, all three files stacked (ReplayCodeView's `stacked` prop -
            see its own comment for why this type needs it while every other type keeps the
            tabbed replay view). */}
        {isCompareJsLayout && (
          <>
            <div className="ws-panel-left" style={{ width: `${layout.leftWidth}px`, display: 'flex', flexDirection: 'column' }}>
              <ReplayCodeView code={code} isGuiSession enabledFiles={['html', 'css', 'js']} activeFile={activeFile} paneTitle="Your Code" stacked />
            </div>
            <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingLeft} />
          </>
        )}

        {/* Middle Panel */}
        <div className="ws-middle-panel" style={{ minWidth: '400px' }}>
          {isCompareImageLayout ? (
            <>
              {/* Reference row: Task Description + Target image, mirrors Workspace.jsx's
                  non-resizable top row for this layout. */}
              <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '200px' }}>
                <div style={{ flex: 1, minWidth: 0, padding: '12px 16px', overflow: 'auto', backgroundColor: '#ffffff' }}>
                  <ReplayTaskDescription task={task} />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e5e5', backgroundColor: '#ffffff', fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: '#171717' }}>
                    Target
                  </div>
                  <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
                    {guiConfig?.finalImage ? (
                      <img src={guiConfig.finalImage} alt="Target layout the student was trying to reach" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '16px', textAlign: 'center' }}>No target image set.</div>
                    )}
                  </div>
                </div>
              </div>
              {/* IDE/"Your View" row, split at the same percentage the student had dragged it
                  to at this point in the replay. */}
              <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <div style={{ width: `${ideSplitPercent}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  {/* enabledFiles here also includes 'html' (unlike guiConfig.enabledFiles,
                      which only lists what the student could edit) so the replay viewer shows
                      the same read-only HTML tab the student's own screen had - see
                      Workspace.jsx's extraReadOnlyFiles on the live CodeEditor. */}
                  <ReplayCodeView code={code} isGuiSession enabledFiles={['html', ...(guiConfig?.enabledFiles || [])]} activeFile={activeFile} paneTitle="Your CSS" />
                </div>
                <div style={{ width: '6px', flexShrink: 0, backgroundColor: '#e5e5e5' }} />
                <div style={{ width: `${100 - ideSplitPercent}%`, minWidth: 0 }}>
                  <ReplayLivePreview code={code} />
                </div>
              </div>
            </>
          ) : isCompareLayout ? (
            <>
              <div style={{ padding: '12px 16px 0' }}>
                <ReplayTaskDescription task={task} />
              </div>
              {/* Fixed Initial/Target row - Initial is the unedited starter, Target is
                  finalHtml, both sharing the starter's CSS (see guiSessionConfig.js). */}
              <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderTop: '1px solid #e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '160px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ReplayLivePreview code={JSON.stringify({ html: (() => { try { return JSON.parse(starterCode)?.html || '' } catch (e) { return '' } })(), css: parsedCode.css || '', js: '' })} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ReplayLivePreview code={targetCodeForCompareLayout} />
                </div>
              </div>
              {/* Your Code + your own live preview, side by side. */}
              <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <ReplayCodeView code={code} isGuiSession enabledFiles={guiConfig?.enabledFiles} activeFile={activeFile} paneTitle="Your Code" />
                </div>
                <div style={{ flex: 1, minWidth: 0, borderLeft: '1px solid #e5e5e5' }}>
                  <ReplayLivePreview code={code} />
                </div>
              </div>
            </>
          ) : isCompareJsLayout ? (
            // Task / Target view / Your view, stacked - mirrors the live Workspace.jsx's
            // isCompareJsLayout block. No Initial pane (dropped in the redesign); the
            // editor (now showing the starter behavior too, via its own content) lives in
            // the left panel above instead of here.
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
                <ReplayTaskDescription task={task} />
              </div>
              {/* flex: '1 0 480px' mirrors the live Workspace.jsx fix - a long task
                  description can no longer squeeze Target/Your View below a usable height;
                  the outer overflowY:auto scrolls instead once content exceeds the area. */}
              <div style={{ display: 'flex', flexDirection: 'column', flex: '1 0 480px' }}>
                <div style={{ height: `${jsViewSplitPercent}%`, minHeight: 0, borderBottom: '1px solid #e5e5e5' }}>
                  <ReplayLivePreview code={targetCodeForCompareJsLayout} />
                </div>
                <div style={{ height: `${100 - jsViewSplitPercent}%`, minHeight: 0 }}>
                  <ReplayLivePreview code={code} />
                </div>
              </div>
            </div>
          ) : (
            <>
              {notice?.mode === 'banner' && <ReplayNotice notice={notice} />}
              {explanationPrompt != null ? (
                // The terminal is absolutely positioned over the panel's bottom, so the
                // explanation stops above it instead of sliding underneath.
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: layout.terminalHeight }}>
                  {showChanges && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 16px', borderBottom: '1px solid #e5e5e5', background: '#fafafa', fontSize: '0.8rem' }}>
                      <span style={{ fontWeight: 600, color: '#171717' }}>Code</span>
                      <span style={{ color: '#737373' }}>{codeBefore != null ? editCaption : ''}</span>
                    </div>
                  )}
                  <ReplayCodeView code={shownCode} isGuiSession={isGuiSession} activeFile={activeFile} changedLines={changedLines} />
                  <ReplayExplanation
                    prompt={explanationPrompt}
                    text={explanation}
                    before={change?.explanationBefore ?? null}
                    caption={change?.explanationBefore != null ? editCaption : null}
                  />
                </div>
              ) : (
                <ReplayCodeView code={shownCode} isGuiSession={isGuiSession} activeFile={activeFile} changedLines={changedLines} />
              )}
              <ReplayTerminal
                height={layout.terminalHeight}
                output={shownTerminal}
                isRunning={isRunning}
                runLabel={runLabel}
              />
              <div
                className="ws-resizer ws-vertical"
                style={{ position: 'absolute', bottom: layout.terminalHeight, left: 0, right: 0, zIndex: 30 }}
                onMouseDown={layout.startDraggingTerminal}
              />
              {notice?.mode === 'screen' && <ReplayNotice notice={notice} />}
            </>
          )}
        </div>

        <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingRight} />

        {/* Right Panel - chat-only for InitialFinal/InitialFinalCSS, same as the live
            workspace (their Live Preview lives in the middle panel instead - see above). */}
        <div className="ws-panel-right" style={{ width: `${layout.rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
          {isGuiSession && !hidesGradingPanels && (
            <div style={{
              display: 'flex',
              background: '#fafafa',
              borderBottom: '1px solid #e5e5e5',
              padding: '8px 16px',
            }}>
              <div style={{ display: 'flex', backgroundColor: '#f3f4f6', borderRadius: '6px', padding: '2px', border: '1px solid #e5e5e5' }}>
                <button
                  onClick={() => setRightPanelTab('preview')}
                  style={{
                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600, border: 'none', borderRadius: '4px',
                    backgroundColor: rightPanelTab === 'preview' ? '#ffffff' : 'transparent',
                    color: rightPanelTab === 'preview' ? '#171717' : '#737373',
                    boxShadow: rightPanelTab === 'preview' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  Live Preview
                </button>
                <button
                  onClick={() => setRightPanelTab('ai')}
                  style={{
                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600, border: 'none', borderRadius: '4px',
                    backgroundColor: rightPanelTab === 'ai' ? '#ffffff' : 'transparent',
                    color: rightPanelTab === 'ai' ? '#171717' : '#737373',
                    boxShadow: rightPanelTab === 'ai' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  AI Assistant
                </button>
              </div>
            </div>
          )}

          {isGuiSession && !hidesGradingPanels && (
            <div style={{ display: rightPanelTab === 'preview' ? 'block' : 'none', flex: 1, minHeight: 0 }}>
              <ReplayLivePreview code={code} />
            </div>
          )}

          <div style={{ display: (!isGuiSession || hidesGradingPanels || rightPanelTab === 'ai') ? 'contents' : 'none' }}>
            <ReplayChatWindow
              messages={shownMessages}
              currentInput={shownInput}
              isAiProcessing={isAiProcessing}
              startTime={startTime}
              studentName={studentName}
              welcomeMessage={chatWelcome}
              highlightIds={newMessageIds}
            />
          </div>
        </div>
      </div>

      {/* Playback Controls */}
      {!HIDE_REPLAY_CONTROLS && (
        <ReplayControls
          currentTime={playback.currentTime}
          duration={playback.duration}
          isPlaying={playback.isPlaying}
          playbackSpeed={playback.playbackSpeed}
          onTogglePlay={playback.togglePlay}
          onSeek={playback.seek}
          onReset={playback.reset}
          onCycleSpeed={playback.cycleSpeed}
        />
      )}
    </div>
  )
}